'use client'

import { useEffect, useRef } from 'react'

const LERP = 0.07

export function useSmoothParallax(containerRef: React.RefObject<HTMLElement | null>) {
  const target = useRef({ x: 0, y: 0 })
  const current = useRef({ x: 0, y: 0 })
  const frame = useRef<number | null>(null)
  const layersRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const panel = containerRef.current?.parentElement
    if (!panel) return

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const apply = () => {
      if (!layersRef.current) return
      const { x, y } = current.current
      layersRef.current.style.setProperty('--parallax-x', `${x.toFixed(4)}`)
      layersRef.current.style.setProperty('--parallax-y', `${y.toFixed(4)}`)
    }

    const tick = () => {
      current.current.x += (target.current.x - current.current.x) * LERP
      current.current.y += (target.current.y - current.current.y) * LERP
      apply()
      frame.current = requestAnimationFrame(tick)
    }

    const onMove = (event: MouseEvent) => {
      if (reducedMotion) return
      const rect = panel.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      target.current.x = ((event.clientX - rect.left) / rect.width - 0.5) * 2
      target.current.y = ((event.clientY - rect.top) / rect.height - 0.5) * 2
    }

    const onLeave = () => {
      target.current.x = 0
      target.current.y = 0
    }

    frame.current = requestAnimationFrame(tick)
    panel.addEventListener('mousemove', onMove)
    panel.addEventListener('mouseleave', onLeave)

    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current)
      panel.removeEventListener('mousemove', onMove)
      panel.removeEventListener('mouseleave', onLeave)
    }
  }, [containerRef])

  return layersRef
}
